import { Box, Stack, Typography, Skeleton } from '@mui/material';
import { useMemo } from 'react';
import {
  formatCurrency,
  formatNumber,
  formatPercent,
} from 'helpers/formatters';
import { ListReportData } from 'api/types/common';
import { useStoreContext } from 'store';
import { getAttributionModelField } from 'helpers/common';

interface Props {
  data: ListReportData | undefined;
}

const ReportsBlock = (props: Props): JSX.Element | null => {
  const { data } = props;
  const {
    state: { attributionModel },
  } = useStoreContext();

  const values = useMemo(
    () => [
      {
        label: 'Potential Budget',
        value: formatCurrency(data?.sum_budget_potential, 0),
      },
      {
        label: 'Actual Budget',
        value: formatCurrency(data?.sum_budget_actual, 0),
      },
      {
        label: 'Potential Revenue',
        value: formatCurrency(data?.sum_revenue_potential, 0),
      },
      {
        label: 'Actual Revenue',
        value: formatCurrency(
          data?.[
            getAttributionModelField(attributionModel, 'sum_revenue_actual')
          ],
          0,
        ),
      },
      {
        label: 'Gross Profit',
        value: formatCurrency(
          data?.[
            getAttributionModelField(
              attributionModel,
              'sum_gross_profit_actual',
            )
          ],
          0,
        ),
      },
      {
        label: 'Potential ROI',
        value: formatNumber(data?.avg_potential_roi),
      },
      {
        label: 'Actual ROI',
        value: formatNumber(
          data?.[getAttributionModelField(attributionModel, 'avg_roi_actual')],
        ),
      },
      {
        label: 'NCR',
        value: formatPercent(
          data?.[
            getAttributionModelField(
              attributionModel,
              'avg_new_customers_ratio',
            )
          ],
        ),
      },
      {
        label: 'New Customers',
        value: formatNumber(
          data?.[
            getAttributionModelField(attributionModel, 'sum_new_customers')
          ],
        ),
      },
      {
        label: 'Avg CAC',
        value: formatCurrency(
          data?.[getAttributionModelField(attributionModel, 'avg_cac')],
        ),
      },
      {
        label: 'Orders',
        value: formatNumber(
          data?.[
            getAttributionModelField(attributionModel, 'sum_orders_nb_actual')
          ],
        ),
      },
      {
        label: 'Basket size',
        value: formatCurrency(
          data?.[getAttributionModelField(attributionModel, 'avg_basket')],
          0,
        ),
      },
      {
        label: 'Avg CPM',
        value: formatCurrency(data?.avg_cpm, 0),
      },
      {
        label: 'Avg Actual CPM',
        value: formatCurrency(data?.avg_actual_cpm, 0),
      },
      {
        label: 'Avg RPM',
        value: formatCurrency(data?.avg_rpm, 0),
      },
      {
        label: 'Actual CM3',
        value: formatCurrency(
          data?.[getAttributionModelField(attributionModel, 'cm3_actual')],
          0,
        ),
      },
      {
        label: 'Actual CM3 %',
        value: formatPercent(
          data?.[getAttributionModelField(attributionModel, 'cm3_actual_pc')],
        ),
      },
      {
        label: 'Conversion rate %',
        value: formatPercent(
          data?.[
            getAttributionModelField(attributionModel, 'conversion_rate_pc')
          ],
        ),
      },
      {
        label: 'CTR %',
        value: formatPercent(data?.ctr_pc || 0),
      },
      {
        label: 'Avg ER %',
        value: formatPercent(data?.avg_engagement_rate_pc || 0),
      },
    ],
    [data, attributionModel],
  );

  return (
    <Box
      id="table-reports-block"
      p={1.5}
      sx={{
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
        overflowX: 'auto',
      }}
    >
      <Stack spacing={1} direction="row" justifyContent="space-between">
        {values.map((v) => (
          <Box
            key={v.label}
            sx={(theme) => ({
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? theme.palette.grey['900']
                  : theme.palette.grey['100'],
              borderRadius: `${theme.shape.borderRadius}px`,
              p: [1.5, 1],
              textAlign: 'center',
              width: '100%',
            })}
          >
            <Typography variant="h6" color="text.primary">
              {data ? v.value : <Skeleton />}
            </Typography>
            <Typography variant="body1" noWrap>
              {v.label}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default ReportsBlock;
